import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Switch,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Paper
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Loader from "../components/loader/Loader";
import {
  getRewardleSubscription,
  updateRewardleSubscription,
  RewardleSubscriptionPreferences
} from "../services/subscriptionService";

const Header = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const Logo = styled("img")({
  width: 64,
  height: 64,
  marginBottom: 12,
  objectFit: "contain"
});

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontFamily: "'Montserrat', sans-serif"
}));

const SubscriptionItem = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(1.5),
  borderRadius: 6,
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}));

const SubscriptionLabel = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 500,
  color: theme.palette.text.primary,
  fontFamily: "'Montserrat', sans-serif"
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#192F67"
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#192F67"
  }
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#192F67",
  color: "#fff",
  padding: "12px 32px",
  fontSize: 16,
  fontWeight: 600,
  textTransform: "none",
  borderRadius: 8,
  fontFamily: "'Montserrat', sans-serif",
  "&:hover": {
    backgroundColor: "#0f1f4a"
  },
  "&:disabled": {
    backgroundColor: "#ccc"
  }
}));

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  backgroundColor: "#f5f5f5",
  padding: theme.spacing(1),
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  paddingTop: theme.spacing(4)
}));

const ContentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  maxWidth: 550,
  width: "100%",
  margin: theme.spacing(1)
}));

const PageHeader = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: "#192F67",
  marginBottom: theme.spacing(0.5),
  fontFamily: "'Montserrat', sans-serif"
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary,
  fontFamily: "'Montserrat', sans-serif"
}));

const ManageRewardleSubscription: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);
  const imageErrorHandled = useRef(false);

  const [subscriptionData, setSubscriptionData] = useState<{
    patronEmail: string;
    euid: string;
    logo: string;
    preferences: RewardleSubscriptionPreferences;
  }>({
    patronEmail: "",
    euid: "",
    logo: "",
    preferences: {
      GenRewardleUpdEmail: false,
      PersonalActivityEmail: false,
      ExclProductOffersEmail: false,
      SecurityUpdEmail: false,
      PostcoderNewsletterEmail: false
    }
  });

  const loadSubscriptionData = useCallback(
    async (transactionId: string, euidParam: string) => {
      try {
        setLoading(true);
        const data = await getRewardleSubscription(transactionId, euidParam);

        // Single state update to prevent flickering
        setSubscriptionData({
          patronEmail: data.email || "",
          euid: (euidParam || "").trim(),
          logo: data.logo || "",
          preferences: {
            GenRewardleUpdEmail: data.preferences?.GenRewardleUpdEmail || false,
            PersonalActivityEmail:
              data.preferences?.PersonalActivityEmail || false,
            ExclProductOffersEmail:
              data.preferences?.ExclProductOffersEmail || false,
            SecurityUpdEmail: data.preferences?.SecurityUpdEmail || false,
            PostcoderNewsletterEmail:
              data.preferences?.PostcoderNewsletterEmail || false
          }
        });

        setHasLoaded(true);
        setLoading(false);
      } catch (err: any) {
        setError(err.message || "Failed to load subscription preferences");
        setHasLoaded(false);
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const dataParam = (urlParams.get("data") || "").trim();
    const euidParam = (urlParams.get("euid") || "").trim();

    // At least one identifier must exist to load
    if (!dataParam && !euidParam) {
      setError("Invalid subscription link");
      setHasLoaded(false);
      setLoading(false);
      return;
    }
    loadSubscriptionData(dataParam, euidParam);
  }, [loadSubscriptionData]);

  const handleToggle = (field: keyof RewardleSubscriptionPreferences) => {
    setSubscriptionData((prev) => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [field]: !prev.preferences[field]
      }
    }));
  };

  const handleSubmit = async () => {
    try {
      const canSubmit =
        !!subscriptionData.euid?.trim() || !!subscriptionData.patronEmail;

      if (!canSubmit) {
        setError("Invalid subscription link");
        return;
      }
      setSaveLoading(true);
      setError(""); // Clear any previous errors
      await updateRewardleSubscription(
        subscriptionData.patronEmail,
        subscriptionData.euid,
        subscriptionData.preferences
      );

      setShowSuccess(true);
      setSaveLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Auto-hide success message after 10 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 10000);
    } catch (err: any) {
      setError(err.message || "Failed to update preferences");
      setShowSuccess(false); // Hide success message if there's an error
      setSaveLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const canSubmit =
    !!subscriptionData.euid?.trim() || !!subscriptionData.patronEmail;

  return (
    <PageContainer>
      <ContentPaper>
        <PageHeader>
          <PageTitle>Email Preferences</PageTitle>
          <PageSubtitle>Manage Rewardle Subscription</PageSubtitle>
        </PageHeader>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <Loader />
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error.includes("support@rewardle.com") ? (
                  <>
                    There was problem while processing the request. Please try
                    again later or contact Rewardle Support at{" "}
                    <a
                      href="mailto:support@rewardle.com?subject=Happy+Birthday+from+Amble"
                      style={{
                        display: "inline-block",
                        textDecoration: "none",
                        color: "#999999",
                        fontWeight: "bold"
                      }}
                      target="_blank"
                      rel="noreferrer"
                    >
                      support@rewardle.com
                    </a>
                  </>
                ) : (
                  error
                )}
              </Alert>
            )}

            {showSuccess && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight={600} gutterBottom>
                  Thanks for popping by!
                </Typography>
                <Typography variant="body2" gutterBottom>
                  You changed your preferences for{" "}
                  {subscriptionData.patronEmail}
                </Typography>
                <Typography variant="body2">
                  You can re-subscribe if you made an error.
                </Typography>
              </Alert>
            )}

            {hasLoaded && (
              <>
                <Header>
                  <Logo
                    src={
                      subscriptionData.logo || "/static/img/rewardle-logo.png"
                    }
                    alt="Rewardle"
                    onError={(e) => {
                      if (!imageErrorHandled.current) {
                        imageErrorHandled.current = true;
                        // Use a data URI placeholder to prevent infinite loop
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 96 96'%3E%3Crect width='96' height='96' fill='%23192F67'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='24' fill='white'%3ER%3C/text%3E%3C/svg%3E";
                      }
                    }}
                  />
                  <Title>Rewardle Email Preferences</Title>
                </Header>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mb: 2, lineHeight: 1.5 }}
                  >
                    Are you sure you do not wish to receive anymore emails from
                    Rewardle?
                    <br />
                    Please deselect those that you no longer wish to receive.
                  </Typography>
                </Box>

                <Stack spacing={2} divider={<Divider />}>
                  <SubscriptionItem>
                    <SubscriptionLabel>
                      General Rewardle Updates
                    </SubscriptionLabel>
                    <StyledSwitch
                      checked={subscriptionData.preferences.GenRewardleUpdEmail}
                      onChange={() => handleToggle("GenRewardleUpdEmail")}
                    />
                  </SubscriptionItem>

                  <SubscriptionItem>
                    <SubscriptionLabel>
                      Your personal activity
                    </SubscriptionLabel>
                    <StyledSwitch
                      checked={
                        subscriptionData.preferences.PersonalActivityEmail
                      }
                      onChange={() => handleToggle("PersonalActivityEmail")}
                    />
                  </SubscriptionItem>

                  <SubscriptionItem>
                    <SubscriptionLabel>Exclusive offers</SubscriptionLabel>
                    <StyledSwitch
                      checked={
                        subscriptionData.preferences.ExclProductOffersEmail
                      }
                      onChange={() => handleToggle("ExclProductOffersEmail")}
                    />
                  </SubscriptionItem>

                  <SubscriptionItem>
                    <SubscriptionLabel>Security Updates</SubscriptionLabel>
                    <StyledSwitch
                      checked={subscriptionData.preferences.SecurityUpdEmail}
                      onChange={() => handleToggle("SecurityUpdEmail")}
                    />
                  </SubscriptionItem>

                  <SubscriptionItem>
                    <SubscriptionLabel>Postcoder Newsletter</SubscriptionLabel>
                    <StyledSwitch
                      checked={
                        subscriptionData.preferences.PostcoderNewsletterEmail
                      }
                      onChange={() => handleToggle("PostcoderNewsletterEmail")}
                    />
                  </SubscriptionItem>
                </Stack>

                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <SubmitButton
                    onClick={handleSubmit}
                    disabled={saveLoading || !canSubmit}
                    fullWidth
                  >
                    {saveLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Submit"
                    )}
                  </SubmitButton>
                </Box>
              </>
            )}
          </Box>
        )}
      </ContentPaper>
    </PageContainer>
  );
};

export default ManageRewardleSubscription;
